import { compareNullableValues } from '@shigo-ui/utils';
import { Download } from 'lucide-react';
import { useState } from 'react';
import {
  DashboardDataTable,
  DashboardDataTableSortDirection,
  DashboardTableSearchInput,
  useDashboardDataTableState,
  type DashboardDataTableColumn,
  type DashboardDataTableSortState,
} from '@shigo-ui/dashboard-ui';

import type { dashboardCanvasRendererProps } from '@/components/dashboard/DashboardCanvas';
import { WidgetActionButton } from '../../../dashboard/components/DashboardWidgetActions';
import { dashboardT } from '../../../../lib/messages';
import { formatCurrency, formatNumber } from '../../../../lib/format';
import { downloadPublisherInventoryCsv } from '../inventoryCsv';
import type { PublisherDashboardInventoryRow } from '../model';
import type { PublisherDashboardRuntime } from '../widgetRuntime';
import { PublisherDashboardWidgetType, type PublisherDashboardWidget } from '../widgetModel';

export function PublisherInventoryRenderer({
  widget,
  runtime,
}: dashboardCanvasRendererProps<PublisherDashboardWidget, PublisherDashboardRuntime>) {
  const [search, setSearch] = useState('');
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const rows = runtime.inventory.data ?? [];
  // Rows without an id, bundle or domain fall back to their position in the response.
  const fallbackKeys = new Map(rows.map((row, index) => [row, `inventory-${String(index)}`]));
  const filtered = rows.filter(
    (row) =>
      !normalizedSearch ||
      [row.appName, row.appBundleId, row.domain].some((value) =>
        value?.toLocaleLowerCase().includes(normalizedSearch),
      ),
  );
  const table = useDashboardDataTableState({
    initialPageSize: 20,
    totalItems: filtered.length,
    pageSizes: [5, 10, 20, 50, 100],
    disabled: runtime.inventory.isLoading,
  });
  if (widget.type !== PublisherDashboardWidgetType.Inventory) return null;

  const { page, pageSize, sort } = table;
  const sorted = [...filtered].sort((left, right) => compareInventory(left, right, sort));
  const visible = sorted.slice(page * pageSize, page * pageSize + pageSize);

  const exportLabel = dashboardT('publisherDashboard.inventory.export');

  return (
    <DashboardDataTable<PublisherDashboardInventoryRow>
      className="max-w-none"
      tableLabel={dashboardT('publisherDashboard.inventory.title')}
      cardTitle={dashboardT('publisherDashboard.inventory.title')}
      cardActions={
        <>
          <DashboardTableSearchInput
            containerClassName="w-full sm:w-60"
            className="bg-muted h-8 text-xs"
            value={search}
            placeholder={dashboardT('publisherDashboard.inventory.search')}
            aria-label={dashboardT('publisherDashboard.inventory.search')}
            onChange={(event) => {
              setSearch(event.target.value);
              table.resetPage();
            }}
          />
          <WidgetActionButton
            compact
            label={exportLabel}
            icon={<Download className="h-4 w-4" aria-hidden="true" />}
            renderAction={(content, ariaLabel) => (
              <button
                type="button"
                aria-label={ariaLabel}
                disabled={filtered.length === 0}
                onClick={() => {
                  downloadPublisherInventoryCsv(filtered);
                }}
              >
                {content}
              </button>
            )}
          />
        </>
      }
      columns={inventoryColumns}
      items={visible}
      getRowKey={(row) => row.appId ?? row.appBundleId ?? row.domain ?? fallbackKeys.get(row) ?? ''}
      loading={runtime.inventory.isLoading}
      loadingMessage={dashboardT('dashboard.widget.loading')}
      errorMessage={runtime.inventory.isError ? dashboardT('dashboard.widget.error') : undefined}
      emptyMessage={dashboardT('dashboard.widget.empty')}
      minWidthClassName="min-w-[760px]"
      sort={table.sortProps}
      pagination={table.pagination}
    />
  );
}

const inventoryColumns: DashboardDataTableColumn<PublisherDashboardInventoryRow>[] = [
  {
    id: 'inventory',
    header: dashboardT('publisherDashboard.inventory.appOrDomain'),
    sortable: true,
    cell: (row) => row.appBundleId ?? row.domain ?? row.appName ?? '-',
  },
  {
    id: 'adRequests',
    header: dashboardT('dashboard.metric.adRequests'),
    sortable: true,
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (row) => formatNumber(row.adRequests),
  },
  {
    id: 'pubAdImpressions',
    header: dashboardT('dashboard.metric.adImpressions'),
    sortable: true,
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (row) => formatNumber(row.pubAdImpressions),
  },
  {
    id: 'pubRevenueShareIo',
    header: dashboardT('publisherDashboard.inventory.revenueShareIo'),
    sortable: true,
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (row) =>
      row.pubRevenueShareIo === undefined ? '-' : `${(row.pubRevenueShareIo * 100).toFixed(2)}%`,
  },
  {
    id: 'pubRevenue',
    header: dashboardT('dashboard.metric.revenue'),
    sortable: true,
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (row) => formatCurrency(row.pubRevenue),
  },
];

function compareInventory(
  left: PublisherDashboardInventoryRow,
  right: PublisherDashboardInventoryRow,
  sort: DashboardDataTableSortState | undefined,
) {
  if (!sort) return 0;
  const leftValue = inventorySortValue(left, sort.key);
  const rightValue = inventorySortValue(right, sort.key);
  return compareNullableValues(
    leftValue,
    rightValue,
    sort.direction === DashboardDataTableSortDirection.Desc,
  );
}

function inventorySortValue(row: PublisherDashboardInventoryRow, key: string) {
  if (key === 'inventory') return row.appBundleId ?? row.domain ?? row.appName;
  if (key === 'adRequests') return row.adRequests;
  if (key === 'pubAdImpressions') return row.pubAdImpressions;
  if (key === 'pubRevenueShareIo') return row.pubRevenueShareIo;
  if (key === 'pubRevenue') return row.pubRevenue;
  return undefined;
}
